import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ScrollToTop from '../components/ScrollToTop';
import { UserPlus, ArrowRight } from 'lucide-react';

const About: React.FC = () => {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const duration = 2000;
    const steps = 35;
    const increment = Math.ceil(duration / steps);
    
    if (count < 35) {
      const timer = setTimeout(() => {
        setCount(prev => Math.min(prev + 1, 35));
      }, increment);
      
      return () => clearTimeout(timer);
    }
  }, [count]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.2 }
    );

    const teamSection = document.getElementById('our-team');
    if (teamSection) {
      observer.observe(teamSection);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Company History Section */}
      <section id="company-history" className="py-20 mt-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Company History</h2>
              <div className="w-24 h-1 bg-[#ff2910] mb-6"></div>
              <p className="text-gray-600 text-justify">
                Hop Shing's journey began in 1989 when Chairman Louis Mo established his vision for waterproofing excellence. From these foundations, we've grown into a specialist serving diverse construction markets. Our extensive network of international suppliers and worldwide experts enhances our technical expertise, enabling us to overcome even the most challenging construction demands.
              </p>
            </div>
            
            <div className="relative">
              <img 
                src="/assets/images/company_history.jpg" 
                alt="About Our History"
                className="rounded-lg shadow-lg w-full h-[250px] md:h-[350px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#ff2910] text-white p-4 rounded shadow-lg">
                <div className="text-3xl font-bold">{count}+</div>
                <div className="text-sm">Years of Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section id="mission-vision" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="relative h-[300px] rounded-lg overflow-hidden group">
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <h3 className="text-2xl font-bold text-white mb-4 drop-shadow-lg group-hover:opacity-0 transition-opacity duration-300">
                  Our Mission
                </h3>
              </div>
              <img 
                src="/assets/images/Mission.jpg"
                alt="Mission Background"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 scale-100 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-[#d40715] group-hover:bg-opacity-80 transition-all duration-300">
                <div className="h-full flex items-center justify-center p-8">
                  <p className="text-white text-justify opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    Our mission is to provide high-quality services that exceed our clients' expectations, while maintaining the highest standards of professionalism, integrity, and excellence in all that we do.
                  </p>
                </div>
              </div>
            </div>

            <div className="relative h-[300px] rounded-lg overflow-hidden group">
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <h3 className="text-2xl font-bold text-white mb-4 drop-shadow-lg group-hover:opacity-0 transition-opacity duration-300">
                  Our Vision
                </h3>
              </div>
              <img 
                src="/assets/images/Vision.jpg"
                alt="Vision Background"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 scale-100 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-[#d40715] group-hover:bg-opacity-80 transition-all duration-300">
                <div className="h-full flex items-center justify-center p-8">
                  <p className="text-white text-justify opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    We envision being the most trusted name in waterproofing solutions, known for our commitment to excellence, environmental responsibility, and customer satisfaction across Asia and beyond.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Team Section */}
      <section id="our-team" className="py-20">
        <div className="container mx-auto px-4">
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 items-center transition-all duration-1000 transform ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
          }`}>
            <div className="relative group">
              <div className="absolute inset-0 bg-[#d40715] transform -rotate-6 rounded-lg transition-transform duration-300 group-hover:rotate-0"></div>
              <div className="relative overflow-hidden rounded-lg shadow-xl">
                <img 
                  src="/assets/images/Our_Team.jpg"
                  alt="Our Team"
                  className="w-full h-[400px] object-cover transform transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className={`transition-all duration-1000 delay-300 transform ${
                isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
              }`}>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Our Team</h2>
                <div className="w-24 h-1 bg-[#ff2910] mb-6"></div>
                <p className="text-gray-600 text-justify leading-relaxed">
                  At Hop Shing, we pride ourselves on maintaining one of the industry's lowest turnover rates, with several team members who have been with us since our founding. This stability reflects our commitment to creating an environment where skilled professionals can thrive and develop their careers for the long term.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Join Us Section */}
      <section id="join-us" className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="relative overflow-hidden rounded-lg shadow-lg">
              <img 
                src="/assets/images/HSEC_Portfolio_02.jpg"
                alt="Join Our Team"
                className="w-full h-[250px] md:h-[300px] object-cover transform transition-transform duration-700 hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            
            <div>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-4">Join Us</h2>
              <div className="w-24 h-1 bg-[#ff2910] mb-6"></div>
              <p className="text-gray-600 text-justify mb-8">
                We believe in building more than waterproofing systems—we're building futures together. When you join Hop Shing, your contributions today become the foundation for your growth tomorrow, supported by a culture that values expertise, dedication, and collaborative spirit.
              </p>
              <button 
                onClick={() => {
                  navigate('/careers');
                  window.scrollTo(0, 0);
                }}
                className="group flex items-center gap-2 px-6 py-3 border-2 border-[#d40715] text-[#d40715] rounded-lg hover:bg-[#d40715] hover:text-white transition-all duration-300"
              >
                <UserPlus size={20} />
                <span>Our Careers</span>
                <ArrowRight size={20} className="transform transition-transform duration-300 group-hover:translate-x-2" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <ScrollToTop />
      <Footer />
    </div>
  );
};

export default About;